import { z } from 'zod'

/** Which shell the backend was started by. Reported by the backend, not guessed by the UI. */
export const shellKindSchema = z.enum(['desktop', 'web'])
export type ShellKind = z.infer<typeof shellKindSchema>

/**
 * Identifies the machine the backend runs on. PROMPT.md §3.1: when the backend lives on a VPS,
 * SSH connections originate there, not on the user's PC — so the UI has to say where "here" is.
 */
export const backendIdentitySchema = z.object({
  /** `embedded` = inside the Electron main process, `standalone` = its own Node process. */
  kind: z.enum(['embedded', 'standalone']),
  hostname: z.string(),
  /** Display label for the "verbunden über" indicator, e.g. "lokal" or "kontor@vps-1". */
  label: z.string(),
})
export type BackendIdentity = z.infer<typeof backendIdentitySchema>

export const databaseHealthSchema = z.object({
  ok: z.boolean(),
  /** Number of the highest applied migration; 0 means an empty database. */
  schemaVersion: z.number().int().nonnegative(),
  appliedMigrations: z.number().int().nonnegative(),
  file: z.string(),
})
export type DatabaseHealth = z.infer<typeof databaseHealthSchema>

/** PROMPT.md §6.6: the web variant may only run behind HTTPS. */
export const transportSecuritySchema = z.object({
  /** True when the backend listens on a non-loopback address without TLS in front of it. */
  insecure: z.boolean(),
  /** True when KONTOR_ALLOW_INSECURE=1 was set, i.e. the user knowingly accepted it. */
  acknowledged: z.boolean(),
})
export type TransportSecurity = z.infer<typeof transportSecuritySchema>

export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  version: z.string(),
  shell: shellKindSchema,
  backend: backendIdentitySchema,
  startedAt: z.iso.datetime(),
  uptimeSec: z.number().nonnegative(),
  dataDir: z.string(),
  database: databaseHealthSchema,
  transport: transportSecuritySchema,
})
export type HealthResponse = z.infer<typeof healthResponseSchema>
